import React, { useMemo, useCallback, Suspense, type JSX } from "react";

import {
    DataTable,
    DATA_TABLE_MODE_VIEWPORT,
    DEFAULT_COLUMN_BUILDER_ID,
    toColumn,
} from "@impero-com/common/components/dataTable";
import { TableFilter } from "@impero-com/common/components/filter";
import { LeftSideMenu } from "@impero-com/common/components/leftSideMenu";
import { BreadcrumbSpec } from "@impero-com/common/componentTypes/breadcrumb";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    ArrayDataSource,
    DataExtractor,
    DataExtractorGroup,
    DataView,
    ExtractorExpander,
    getAllExtractors,
} from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    DataViewIndex,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    ActionMenu,
    ActionSpec,
    ApplicationBarToolbar,
    ContentWithoutBorder,
    PageColumns,
    PageContent,
    PageLayout,
    PageSpinner,
} from "@impero-com/ui";

import {
    PageStateManager,
    RenderContentArgs,
} from "org/pageState/components/pageStateManager";
import {
    pageStateReportsCodecs,
    ReportsPageStateRaw,
    ReportsPageStateType,
} from "org/pageState/convert/reports";
import { PageKeyReports, PAGE_KEY } from "org/pageState/pageKey";
import { isAccessRightCategoryExtractorId } from "utils/accessRights";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { OrgApplicationBarHeader } from "../../../components/applicationBar/OrgApplicationBarHeader";
import { WebRoute } from "../../../routes";
import { useNewMenu } from "../../navigation/useNewMenu";
import { useReportsQuery } from "../queries";
import type { Report, ReportType } from "../types";
import { convertStringToReportColumnIdOrThrow } from "../utils";

import {
    getReportName,
    NameCell,
    PushReportsCountCell,
    ActionCell,
    ReportColumnId,
    REPORT_COLUMN_IDS,
    DatasheetCell,
    AccessRightsCell,
    buildExtractorsAndExpanders,
} from "./columns";
import { ReportsMenuLayout } from "./menuLayout";
import {
    buildNavigationItems,
    countReports,
    pageTypeToNavItemId,
    ReportsCount,
} from "./navigation";

type ReportListPageType = "All" | ReportType;

interface ReportsListLayoutProps {
    /**
     * Type of the page.
     * Determines the title of the page and the navigation item that should be selected,
     * as well ass which report types will be shown in the table.
     */
    pageType: ReportListPageType;

    /**
     * Column IDs to be shown in the table.
     * The order of the columns is determined by the order of the IDs in this array.
     * Duplications are allowed.
     */
    columnIds: Array<ReportColumnId>;

    /**
     * The actions shown in the application bar menu.
     */
    actions: Array<ActionSpec>;
}

function ReportsListLayout({
    pageType,
    columnIds,
    actions,
}: ReportsListLayoutProps): JSX.Element {
    const title = useMemo(() => localizePageTitle(pageType), [pageType]);
    const newMenu = useNewMenu();

    const { data: reports } = useReportsQuery();
    const reportsCount = countReports(reports);
    const shownReports = filterReportsByType(reports, pageType);

    const renderError = (code: number | undefined) => {
        return (
            <OrgApplicationBar title={title}>
                <LoggedInHTTPError code={code} />
            </OrgApplicationBar>
        );
    };

    return (
        <Suspense fallback={<PageSpinner />}>
            <PageStateManager<ReportsPageStateType, ReportsPageStateRaw>
                pageKey={buildPageKeyForReportPageType(pageType)}
                renderLoading={() => <PageSpinner />}
                renderError={renderError}
                codecs={pageStateReportsCodecs}
                renderContent={(
                    args: RenderContentArgs<ReportsPageStateRaw>
                ) => {
                    const { pageState, onPageStateChange } =
                        toStrictRenderContentArgs(args);
                    const table = (
                        <ReportsTable
                            pageType={pageType}
                            reports={shownReports}
                            columnIds={columnIds}
                            visibleColumnIds={pageState.visibleColumnIds}
                            onVisibleColumnIdsChanged={(
                                newVisibleColumnIds
                            ) => {
                                onPageStateChange({
                                    ...pageState,
                                    visibleColumnIds: newVisibleColumnIds,
                                });
                            }}
                            filter={pageState.filter}
                            onFilterChanged={(newFilter) => {
                                onPageStateChange({
                                    ...pageState,
                                    filter: newFilter,
                                });
                            }}
                        />
                    );

                    if (newMenu) {
                        return (
                            <ReportsMenuLayout
                                pageType={pageType}
                                reportsCount={reportsCount}
                                actions={actions}
                            >
                                {table}
                            </ReportsMenuLayout>
                        );
                    }

                    return (
                        <ReportsApplicationBar
                            title={title}
                            reportsCount={reportsCount}
                            pageType={pageType}
                            actions={actions}
                            leftSideMenuCollapsed={
                                pageState.leftSideMenuCollapsed
                            }
                            onLeftSideMenuCollapsedChange={(
                                newCollapsed: boolean
                            ) => {
                                onPageStateChange({
                                    ...pageState,
                                    leftSideMenuCollapsed: newCollapsed,
                                });
                            }}
                        >
                            {table}
                        </ReportsApplicationBar>
                    );
                }}
            />
        </Suspense>
    );
}

function filterReportsByType(
    allReports: Array<Report>,
    reportType: ReportListPageType
): Array<Report> {
    if (reportType === "All") {
        return allReports;
    }
    return allReports.filter((report) => report.type === reportType);
}

interface ReportsApplicationBarProps {
    children: React.ReactNode;
    title: string;
    reportsCount: ReportsCount;
    pageType: ReportListPageType;
    actions: Array<ActionSpec>;
    leftSideMenuCollapsed: boolean;
    onLeftSideMenuCollapsedChange: (collapsed: boolean) => void;
}

function ReportsApplicationBar({
    children,
    title,
    reportsCount,
    pageType,
    actions,
    leftSideMenuCollapsed,
    onLeftSideMenuCollapsedChange,
}: ReportsApplicationBarProps): JSX.Element {
    const breadCrumbs = useMemo(() => {
        return buildAppBarBreadCrumbSpec(title);
    }, [title]);

    const navigationItems = useMemo(() => {
        return buildNavigationItems(reportsCount, pageType);
    }, [reportsCount, pageType]);

    return (
        <PageLayout fullHeight>
            <OrgApplicationBarHeader title={breadCrumbs}>
                <ApplicationBarToolbar
                    actions={
                        <ActionMenu
                            id="action-menu-reports"
                            actions={actions}
                        />
                    }
                />
            </OrgApplicationBarHeader>
            <PageColumns>
                <LeftSideMenu
                    items={navigationItems}
                    selectedItemId={pageTypeToNavItemId(pageType)}
                    onItemSelected={() => {}}
                    collapsed={leftSideMenuCollapsed}
                    onCollapsedChange={onLeftSideMenuCollapsedChange}
                />
                <PageContent>{children}</PageContent>
            </PageColumns>
        </PageLayout>
    );
}

function buildAppBarBreadCrumbSpec(title: string): BreadcrumbSpec {
    return {
        title,
        breadCrumbs: [
            {
                label: t("Report_BreadCrumb"),
                url: WebRoute.REPORTS,
            },
        ],
    };
}

interface ReportsTableProps {
    pageType: ReportListPageType;

    /**
     * Reports to be shown in the table.
     */
    reports: Array<Report>;

    /**
     * All column IDs that can be shown in the table (but not necessary all of them are visible).
     */
    columnIds: Array<ReportColumnId>;

    /**
     * Column IDs that are currently visible in the table.
     */
    visibleColumnIds: Set<ReportColumnId>;

    /**
     * Callback that is called when the set of visible column IDs changes.
     */
    onVisibleColumnIdsChanged: (
        newVisibleColumnIds: Set<ReportColumnId>
    ) => void;

    /**
     * The filter to be applied to the reports.
     */
    filter: SimpleAndFilter | null;

    /**
     * Callback that is called when the filter changes.
     */
    onFilterChanged: (newFilter: SimpleAndFilter | null) => void;
}

function ReportsTable({
    pageType,
    reports,
    columnIds,
    visibleColumnIds,
    onVisibleColumnIdsChanged,
    filter,
    onFilterChanged,
}: ReportsTableProps): JSX.Element {
    const [dataView, dataViewIndex] = useMemo(() => {
        const { extractorGroups, expanders } = buildExtractorsAndExpanders(
            pageType,
            columnIds,
            reports
        );
        return buildDataViewAndIndex(reports, extractorGroups, expanders);
    }, [columnIds, reports, pageType]);

    const queryResult = useMemo(() => {
        return dataViewIndex.find(filter);
    }, [dataViewIndex, filter]);

    const onAddFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const newFilter = addFieldFilter(
            extractorId,
            fieldFilter,
            excluded,
            filter
        );
        onFilterChanged(newFilter);
    };
    const onRemoveFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        if (filter === null) {
            return;
        }
        const newFilter = removeFieldFilter(
            extractorId,
            fieldFilter,
            excluded,
            filter
        );
        onFilterChanged(newFilter);
    };
    const onResetFilter = () => onFilterChanged(null);

    // Convert the callback in a type-safe manner from:
    //     (Set<ReportColumnId>) => void
    // to:
    //     (Set<string>) => void
    const onVisibleColumnsChanged = useCallback(
        (newColumns: Set<string>) => {
            const newColumnIds = new Set<ReportColumnId>(
                Array.from(newColumns).map(convertStringToReportColumnIdOrThrow)
            );
            onVisibleColumnIdsChanged(newColumnIds);
        },
        [onVisibleColumnIdsChanged]
    );

    return (
        <ContentWithoutBorder>
            <TableFilter<Report>
                id="filter"
                queryResult={queryResult}
                dataViewIndex={dataViewIndex}
                filter={filter}
                extractorRefs={getAllExtractors(dataView).map((ext) => ({
                    extractorId: ext.id,
                }))}
                onAddFilter={onAddFilter}
                onRemoveFilter={onRemoveFilter}
                onResetFilter={onResetFilter}
            />
            <DataTable<Report>
                id="reports-list"
                dataView={queryResult.dataView}
                mode={DATA_TABLE_MODE_VIEWPORT}
                onVisibleColumnsChanged={onVisibleColumnsChanged}
                visibleColumnIds={visibleColumnIds}
                tools={[
                    { type: "Spacer", flex: "auto" },
                    { type: "ViewAdjuster" },
                ]}
                columnBuilders={{
                    [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
                        if (isAccessRightCategoryExtractorId(extractor.id)) {
                            return { display: "NEVER" };
                        }
                        return toColumn(extractor, {
                            display: "DEFAULT_VISIBLE",
                        });
                    },
                    [REPORT_COLUMN_IDS.NAME]: (
                        _extractor: DataExtractor<Report, string>
                    ) => ({
                        getFormattedValue: (report: Report) => (
                            <NameCell report={report} pageType={pageType} />
                        ),
                    }),
                    [REPORT_COLUMN_IDS.NAME_FOR_ALL]: (
                        _extractor: DataExtractor<Report, string>
                    ) => ({
                        getFormattedValue: (report: Report) => (
                            <NameCell report={report} pageType={pageType} />
                        ),
                    }),
                    [REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT]: (
                        _extractor: DataExtractor<Report, number | null>
                    ) => ({
                        getFormattedValue: (report: Report) => (
                            <PushReportsCountCell report={report} />
                        ),
                    }),
                    [REPORT_COLUMN_IDS.ACTION]: (
                        _extractor: DataExtractor<Report, null>
                    ) => ({
                        getFormattedValue: (report: Report) => (
                            <ActionCell report={report} />
                        ),
                    }),
                    [REPORT_COLUMN_IDS.DATASHEET]: (
                        _extractor: DataExtractor<Report, string>
                    ) => ({
                        getFormattedValue: (report: Report) => (
                            <DatasheetCell
                                report={report}
                                pageType={pageType}
                            />
                        ),
                    }),
                    [REPORT_COLUMN_IDS.ACCESS_RIGHTS]: (_extractor) => ({
                        getFormattedValue: (report: Report) => (
                            <AccessRightsCell report={report} />
                        ),
                    }),
                }}
            />
        </ContentWithoutBorder>
    );
}

function buildDataViewAndIndex(
    reports: Array<Report>,
    extractorGroups: Array<DataExtractorGroup<Report>>,
    expanders: Array<ExtractorExpander<unknown, unknown>>
): [DataView<Report>, DataViewIndex<Report>] {
    const dataView: DataView<Report> = {
        dataSource: new ArrayDataSource(reports, getReportIdForIndex),
        extractorGroups,
        sorters: [],
        defaultCompareItems: Cmp.map(getReportName, Cmp.primitive),
    };
    const indexedExtractorIds = extractorGroups.flatMap((group) =>
        group.extractors.map((ext) => ext.id)
    );
    const index = new DataViewIndex(dataView, indexedExtractorIds, expanders);
    return [dataView, index];
}

function getReportIdForIndex(report: Report): string {
    // Note: SavedReport.id as well as DataSheetReport.dataSheetId are UUIDs,
    // so we don't need to worry about a potential clash.
    switch (report.type) {
        case "Status":
        case "Workload":
        case "Classification": {
            return report.data.id;
        }
        case "Datasheet": {
            return report.data.dataSheetId;
        }
        default: {
            return assertNever(report);
        }
    }
}

function localizePageTitle(pageType: ReportListPageType): string {
    switch (pageType) {
        case "All":
            return t("Report_BreadCrumb_All");
        case "Status":
            return t("Report_BreadCrumb_Status");
        case "Workload":
            return t("Report_BreadCrumb_Workload");
        case "Classification":
            return t("Report_BreadCrumb_Classification");
        case "Datasheet":
            return t("Report_BreadCrumb_Datasheet");
        default:
            return assertNever(pageType);
    }
}

function buildPageKeyForReportPageType(
    reportPageType: ReportListPageType
): PageKeyReports {
    switch (reportPageType) {
        case "All":
            return PAGE_KEY.reportsAll();
        case "Status":
            return PAGE_KEY.reportsStatus();
        case "Workload":
            return PAGE_KEY.reportsWorkload();
        case "Classification":
            return PAGE_KEY.reportsClassification();
        case "Datasheet":
            return PAGE_KEY.reportsDatasheet();
        default:
            return assertNever(reportPageType);
    }
}

/**
 * A stricter version of RenderContentArgs<ReportsPageStateRaw> that assumes that
 * pageState and onPageStateChange are always defined.
 */
interface StrictRenderContentArgs {
    pageState: ReportsPageStateRaw;
    onPageStateChange: (pageState: ReportsPageStateRaw) => void;
}

/**
 * This function is used to convert the arguments of renderContent function in PageStateManager
 * to a more strict type, which is used in ReportsListLayout.
 */
function toStrictRenderContentArgs(
    args: RenderContentArgs<ReportsPageStateRaw>
): StrictRenderContentArgs {
    const { pageState, onPageStateChange } = args;
    if (pageState === undefined) {
        throw new Error(
            "Expected pageState to be defined in context of ReportsListLayout"
        );
    }
    if (onPageStateChange === undefined) {
        throw new Error(
            "Expected onPageStateChange to be defined in context of ReportsListLayout"
        );
    }
    return { pageState, onPageStateChange };
}

export { ReportsListLayout };

export type { ReportsListLayoutProps, ReportListPageType };
